/**
 * Interpreting a failed sign-in response.
 *
 * The SPA and the API are separate Railway services built from one repo, so every
 * release has a window where they are out of step. One direction of that skew is
 * dangerous: this build keeps the captcha hidden until the server asks for it, but a
 * not-yet-updated backend demands a token on every attempt and refuses without
 * setting `captchaRequired`. If the form only trusted that flag, the widget would
 * never appear during the window and NOBODY could sign in.
 *
 * So the flag is the contract, and the status/message shape is a compatibility
 * fallback for the older backend.
 */

interface LoginErrorLike {
  response?: {
    status?: number;
    data?: { captchaRequired?: boolean; message?: unknown };
  };
}

/**
 * True when the server refused this attempt for a captcha reason and the user needs
 * the widget in order to proceed.
 *
 * A bare 400 is deliberately NOT enough — that is also how validation failures come
 * back, and surfacing a captcha because someone mistyped their email would be its own
 * small confusion. The message has to actually be about the check.
 */
export function isCaptchaRefusal(err: unknown): boolean {
  const response = (err as LoginErrorLike)?.response;
  if (!response) return false;

  if (response.data?.captchaRequired === true) return true;

  // The captcha middleware is the only thing that answers a sign-in with 403;
  // bad credentials are 401.
  if (response.status === 403) return true;

  if (response.status === 400) {
    return /captcha|security check/i.test(String(response.data?.message ?? ''));
  }

  return false;
}
